import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Notify, toast } from "@/components/ui/notify";
import type { CreateProductRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { ProductForm } from "@/features/products/ProductForm";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { useAuthStore } from "@/stores/auth.store";
import { useProducts, useCreateProduct, useDeleteProduct } from "@/features/products/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";

const formatBRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export function ProductsPage() {
  const me = useAuthStore((s) => s.user)!;
  const productsQuery = useProducts();
  const { data: products = [], isLoading } = productsQuery;
  const createProduct = useCreateProduct();
  const deleteProduct = useDeleteProduct();
  const [showForm, setShowForm] = useState(false);

  async function onSubmit(values: CreateProductRequest) {
    try {
      await createProduct.mutateAsync(values);
      toast.success("Produto criado.");
      setShowForm(false);
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao criar produto.", { description: msg });
    }
  }

  async function onDelete(id: string, name: string) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Excluir produto "${name}"?`,
      confirmLabel: "Excluir",
      typeToConfirm: name,
    });
    if (!ok) return;
    try {
      await deleteProduct.mutateAsync(id);
      toast.success("Produto excluído.");
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao excluir.", { description: msg });
    }
  }

  return (
    <section>
      <PageHeader
        title="Mix de Produtos"
        subtitle="SKUs disponíveis para execução nas linhas."
        actions={
          me.role === "GESTOR" ? (
            <Button onClick={() => setShowForm((v) => !v)}>
              {showForm ? "Cancelar" : "Novo produto"}
            </Button>
          ) : null
        }
      />

      {showForm && me.role === "GESTOR" ? (
        <ProductForm isPending={createProduct.isPending} onSubmit={onSubmit} />
      ) : null}

      {productsQuery.isError ? (
        <Notify.QueryError
          error={productsQuery.error}
          onRetry={() => void productsQuery.refetch()}
        />
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        <DataTable
          data={products}
          rowKey={(p) => p.id}
          emptyState={
            me.role === "GESTOR"
              ? "Cadastre o primeiro produto com o botão acima."
              : "Nenhum produto cadastrado."
          }
          columns={[
            { header: "Nome", accessor: (p) => p.name },
            { header: "Unidade", accessor: (p) => p.unit, className: "text-muted-foreground" },
            {
              header: "Contado em",
              accessor: (p) => `${p.countingUnit} · ${Number(p.unitFactor)}×`,
              className: "text-muted-foreground",
            },
            {
              header: "Preço unitário",
              accessor: (p) => formatBRL.format(Number(p.pricePerUnit)),
              className: "font-mono",
            },
            {
              header: "Descrição",
              accessor: (p) => p.description ?? "—",
              className: "text-muted-foreground",
            },
            {
              header: "",
              accessor: (p) =>
                me.role === "GESTOR" ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Excluir ${p.name}`}
                    onClick={() => void onDelete(p.id, p.name)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null,
              className: "text-right",
            },
          ]}
        />
      )}
    </section>
  );
}
